/**
 * Utility per formattare i sottotitoli dei periodi nei grafici
 * Supporta EN, IT, DE
 */

const monthNames = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    it: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
    de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']
};

/**
 * Formatta una data singola (giorno singolo)
 * Formato: "Date: Jan 06, 2026 • Today" (EN)
 */
export function formatSingleDaySubtitle(date, locale = 'en') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    const isToday = compareDate.getTime() === today.getTime();
    
    const month = monthNames[locale][date.getMonth()];
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    
    switch (locale) {
        case 'it':
            return `Data: ${day} ${month} ${year}${isToday ? ' • Oggi' : ''}`;
        case 'de':
            return `Datum: ${day}. ${month} ${year}${isToday ? ' • Heute' : ''}`;
        default: // en
            return `Date: ${month} ${day}, ${year}${isToday ? ' • Today' : ''}`;
    }
}

/**
 * Formatta un periodo intraday (ore nello stesso giorno)
 * Formato: "Jan 06 | 11:00 - 15:00 • Last 4h" (EN)
 */
export function formatIntradaySubtitle(date, startHour, endHour, hours, locale = 'en') {
    const month = monthNames[locale][date.getMonth()];
    const day = date.getDate().toString().padStart(2, '0');
    
    const startTime = `${startHour.toString().padStart(2, '0')}:00`;
    const endTime = `${endHour.toString().padStart(2, '0')}:00`;
    
    switch (locale) {
        case 'it':
            return `${day} ${month} | ${startTime} - ${endTime} • ${hours === 1 ? 'Ultima ora' : `Ultime ${hours}h`}`;
        case 'de':
            return `${day}. ${month} | ${startTime} - ${endTime} • ${hours === 1 ? 'Letzte Std.' : `Letzte ${hours} Std.`}`;
        default: // en
            return `${month} ${day} | ${startTime} - ${endTime} • Last ${hours}h`;
    }
}

/**
 * Formatta un periodo multi-day
 * Formato: "Period: Jan 04 - Jan 06 • 3 Days" (EN)
 */
export function formatMultiDaySubtitle(startDate, endDate, days, locale = 'en') {
    const startMonth = monthNames[locale][startDate.getMonth()];
    const startDay = startDate.getDate().toString().padStart(2, '0');
    const endMonth = monthNames[locale][endDate.getMonth()];
    const endDay = endDate.getDate().toString().padStart(2, '0');
    
    switch (locale) {
        case 'it':
            return `Periodo: ${startDay} ${startMonth} - ${endDay} ${endMonth} • ${days} ${days === 1 ? 'Giorno' : 'Giorni'}`;
        case 'de':
            return `Zeitraum: ${startDay}. ${startMonth} - ${endDay}. ${endMonth} • ${days} ${days === 1 ? 'Tag' : 'Tage'}`;
        default: // en
            return `Period: ${startMonth} ${startDay} - ${endMonth} ${endDay} • ${days} ${days === 1 ? 'Day' : 'Days'}`;
    }
}

/**
 * Formatta il sottotitolo del periodo per un grafico
 * Gestisce automaticamente tutti i casi (singolo giorno, intraday, multi-day)
 */
export function getChartPeriodSubtitle(startDate, endDate, locale = 'en') {
    if (!startDate || !endDate) {
        return '';
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Normalizza le date a mezzanotte per confronto
    const startDay = new Date(start);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);
    
    const diffMs = endDay - startDay;
    // Per range inclusivi: se startDay e endDay sono uguali = 1 giorno
    // Se sono diversi, conta i giorni tra di loro + 1 (inclusivo)
    const diffDays = diffMs === 0 ? 1 : Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
    
    // Caso 1: Stesso giorno
    if (diffDays === 1) {
        // Verifica se è intraday (ha ore specifiche)
        const startHour = start.getHours();
        const endHour = end.getHours();
        
        // Se le ore sono diverse da 0 e 23, è intraday
        if ((startHour !== 0 || endHour !== 23) && (startHour !== endHour)) {
            const hours = endHour - startHour;
            return formatIntradaySubtitle(start, startHour, endHour, hours, locale);
        }
        
        // Altrimenti è un giorno singolo completo
        return formatSingleDaySubtitle(end, locale);
    }
    
    // Caso 2: Multi-day
    return formatMultiDaySubtitle(start, end, diffDays, locale);
}

/**
 * Ottiene la configurazione del sottotitolo per ApexCharts
 */
export function getChartSubtitleConfig(startDate, endDate, locale = 'en') {
    const text = getChartPeriodSubtitle(startDate, endDate, locale);
    
    return {
        text: text,
        align: 'left',
        margin: 10,
        offsetX: 0,
        offsetY: 0,
        floating: false,
        style: {
            fontSize: '12px',
            fontWeight: 'normal',
            color: '#666'
        }
    };
}

/**
 * Helper per aggiornare chartOptions esistenti con il sottotitolo del periodo
 */
export function addPeriodSubtitleToChartOptions(chartOptions, startDate, endDate, locale = 'en') {
    return {
        ...chartOptions,
        subtitle: getChartSubtitleConfig(startDate, endDate, locale)
    };
}
